// Estado das edições da planilha na tela, por aba.
// Guarda uma cópia editável das linhas de cada aba + dois conjuntos de células tocadas:
// `salvas` (camada de correção que já está no banco, veio pronta no source) e
// `pendentes` (o que o usuário mexeu agora e ainda não gravou). Reseta quando os dados
// de origem mudam (troca de lote / filtro de status). O que ele devolve em `grids` é o
// que alimenta o reexport do Excel — com as correções aplicadas.
import { useCallback, useMemo, useState } from 'react';
import { TAB_KEYS, type TabKey, type WorkbookGrids, type GridModel } from './faturaGridModel';

export { TAB_KEYS };
export type { TabKey };

const cloneRows = (rows: (string | number)[][]) => rows.map(r => [...r]);
const cellKey = (r: number, c: number) => `${r}:${c}`;

/** Uma célula alterada nesta sessão, pronta para virar payload de gravação. */
export interface EdicaoPendente { tab: TabKey; r: number; c: number; label: string; valor: string | number; }

interface TabEdits { rows: (string | number)[][]; salvas: Set<string>; pendentes: Set<string>; }
type EditsState = Partial<Record<TabKey, TabEdits>>;

function initState(source: WorkbookGrids): EditsState {
  const st: EditsState = {};
  for (const k of TAB_KEYS) {
    const g = source[k];
    if (g) st[k] = { rows: cloneRows(g.rows), salvas: new Set(g.corrigidas || []), pendentes: new Set() };
  }
  return st;
}

const VAZIO = new Set<string>();

export function useGridEdits(source: WorkbookGrids) {
  // `source` deve vir memoizado pelo chamador (useMemo por faturas/filtro) — a
  // identidade dele é a chave do reset via useMemo abaixo.
  const [state, setState] = useState<EditsState>(() => initState(source));
  const [sourceRef, setSourceRef] = useState(source);
  if (sourceRef !== source) {           // origem trocou → reidrata (padrão "derive on render")
    setSourceRef(source);
    setState(initState(source));
  }

  const onCellChange = useCallback((tab: TabKey, r: number, c: number, raw: string | number) => {
    setState(prev => {
      const cur = prev[tab];
      if (!cur) return prev;
      const rows = cur.rows.map((row, i) => (i === r ? row.map((v, j) => (j === c ? raw : v)) : row));
      const pendentes = new Set(cur.pendentes); pendentes.add(cellKey(r, c));
      return { ...prev, [tab]: { ...cur, rows, pendentes } };
    });
  }, []);

  /** Descarta o que ainda não foi salvo — as correções já gravadas continuam de pé. */
  const resetAll = useCallback(() => setState(initState(source)), [source]);

  /** Depois de gravar: só as células REALMENTE gravadas saem de pendente (sem esperar
   *  o recarregamento do pai). O que falhou continua pendente, e visível em âmbar. */
  const marcarSalvas = useCallback((chaves: { tab: TabKey; r: number; c: number }[]) => {
    setState(prev => {
      const porAba = new Map<TabKey, Set<string>>();
      for (const ch of chaves) {
        const set = porAba.get(ch.tab) || new Set<string>();
        set.add(cellKey(ch.r, ch.c));
        porAba.set(ch.tab, set);
      }
      const out: EditsState = {};
      for (const k of TAB_KEYS) {
        const cur = prev[k];
        if (!cur) continue;
        const gravadas = porAba.get(k);
        if (!gravadas?.size) { out[k] = cur; continue; }
        out[k] = {
          rows: cur.rows,
          salvas: new Set([...cur.salvas, ...gravadas]),
          pendentes: new Set([...cur.pendentes].filter(x => !gravadas.has(x))),
        };
      }
      return out;
    });
  }, []);

  const pendentesCount = useMemo(
    () => TAB_KEYS.reduce((n, k) => n + (state[k]?.pendentes.size || 0), 0),
    [state],
  );
  const salvasCount = useMemo(
    () => TAB_KEYS.reduce((n, k) => n + (state[k]?.salvas.size || 0), 0),
    [state],
  );

  /** Todas as células alteradas agora, com o rótulo da coluna — payload da gravação. */
  const coletarPendentes = useCallback((): EdicaoPendente[] => {
    const out: EdicaoPendente[] = [];
    for (const k of TAB_KEYS) {
      const cur = state[k];
      const cols = source[k]?.columns;
      if (!cur || !cols) continue;
      for (const chave of cur.pendentes) {
        const [r, c] = chave.split(':').map(Number);
        out.push({ tab: k, r, c, label: cols[c]?.label ?? '', valor: cur.rows[r]?.[c] ?? '' });
      }
    }
    return out;
  }, [state, source]);

  // WorkbookGrids com as linhas editadas (colunas/bandas/severityCol preservados).
  const grids: WorkbookGrids = useMemo(() => {
    const out = {} as WorkbookGrids;
    for (const k of TAB_KEYS) {
      const g = source[k];
      out[k] = g ? ({ ...g, rows: state[k]?.rows ?? g.rows } as GridModel) : null;
    }
    return out;
  }, [source, state]);

  const rowsFor = (tab: TabKey) => state[tab]?.rows ?? source[tab]?.rows ?? [];
  /** Todas as células marcadas (salvas + pendentes) — é o que a grade contorna. */
  const editedFor = (tab: TabKey) => {
    const cur = state[tab];
    if (!cur) return VAZIO;
    if (!cur.pendentes.size) return cur.salvas;
    return new Set([...cur.salvas, ...cur.pendentes]);
  };
  const pendentesFor = (tab: TabKey) => state[tab]?.pendentes ?? VAZIO;

  return {
    grids, onCellChange, resetAll, marcarSalvas, coletarPendentes,
    pendentesCount, salvasCount, editedCount: pendentesCount + salvasCount,
    rowsFor, editedFor, pendentesFor, cellKey,
  };
}
